import {useMemo, useState} from 'react'
import {Modal, Button, Form, Spinner} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {ASSIGNABLE_TARGETS} from '../../../gql/queries/twilioNumbers'
import {ASSIGN_PROVISIONED_NUMBER} from '../../../gql/mutations/twilioNumbers'

type Target = {id: number; name: string; email?: string}

type Props = {
  show: boolean
  onHide: () => void
  numberId: string | null
  numberLabel?: string
  currentAssignee?: string
  onAssigned?: () => void
}

// Assign a number's routing to a user or a group (round-robin member).
// Billing stays with the owner; this only changes where it rings / dials out.
export const AssignNumberModal = ({show, onHide, numberId, numberLabel, currentAssignee, onAssigned}: Props) => {
  const {data, loading} = useQuery(ASSIGNABLE_TARGETS, {fetchPolicy: 'cache-and-network', skip: !show})
  const [assign, {loading: assigning}] = useMutation(ASSIGN_PROVISIONED_NUMBER)

  const targets = data?.assignableTargets ?? {groups: [], users: []}
  const users: Target[] = targets.users ?? []
  const groups: Target[] = targets.groups ?? []

  const [mode, setMode] = useState<'user' | 'group'>('user')
  const [targetId, setTargetId] = useState('')

  const options = useMemo(() => (mode === 'user' ? users : groups), [mode, users, groups])

  const submit = async () => {
    if (!numberId || !targetId) {
      toast.warning('Pick a destination first.')
      return
    }
    try {
      const {data: res} = await assign({
        variables: {
          numberId,
          targetUserId: mode === 'user' ? targetId : null,
          targetGroupId: mode === 'group' ? targetId : null,
        },
      })
      const out = res?.assignProvisionedNumber
      if (out?.success) {
        toast.success(out.message || 'Number assigned.')
        ;(out.warnings || []).forEach((w: string) => toast.warning(w))
        setTargetId('')
        onAssigned?.()
        onHide()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not assign the number')
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Assign {numberLabel || 'number'}
          {currentAssignee ? <div className='text-muted fs-7 fw-normal'>Currently: {currentAssignee}</div> : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && !data ? (
          <div className='text-center py-4'>
            <Spinner animation='border' size='sm' />
          </div>
        ) : (
          <>
            <div className='mb-3 d-flex gap-4'>
              <Form.Check
                type='radio'
                id='assign-mode-user'
                label='A user'
                checked={mode === 'user'}
                onChange={() => {
                  setMode('user')
                  setTargetId('')
                }}
              />
              <Form.Check
                type='radio'
                id='assign-mode-group'
                label='A group (round-robin)'
                checked={mode === 'group'}
                onChange={() => {
                  setMode('group')
                  setTargetId('')
                }}
              />
            </div>
            <Form.Select value={targetId} onChange={(e: any) => setTargetId(e.target.value)}>
              <option value=''>{mode === 'user' ? 'Select a user…' : 'Select a group…'}</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                  {mode === 'user' && o.email ? ` · ${o.email}` : ''}
                </option>
              ))}
            </Form.Select>
            {mode === 'group' && (
              <div className='text-muted fs-8 mt-2'>
                The number will ring the group member with the fewest leads today.
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onHide} disabled={assigning}>
          Cancel
        </Button>
        <Button variant='primary' onClick={submit} disabled={assigning || !targetId}>
          {assigning ? <Spinner animation='border' size='sm' /> : 'Assign'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
